import type { CategoryPageData } from './types'
import WhyImage from '@/assets/images/refresh/clinitian-hiring/why.jpg'
import GeneralMedicinePhone from '@/assets/images/general-medicine-phone.png'

import SleepSupportImg from '@/assets/images/refresh/categories/support-cards/sleep-support.webp'
import WeigthMngtImg from '@/assets/images/refresh/categories/support-cards/weight-management.webp'
import MoodMentalHealthImg from '@/assets/images/refresh/categories/support-cards/mood-mental-health.webp'
import MemoryFocusImg from '@/assets/images/refresh/categories/support-cards/memory-focus.webp'
import HairLossImg from '@/assets/images/refresh/categories/support-cards/hair-loss.webp'
import SkinHealthImg from '@/assets/images/refresh/categories/support-cards/skin-health.webp'

import WhyImg2 from '@/assets/images/refresh/categories/why-gm/why-1.webp'
import WhyImg1 from '@/assets/images/refresh/categories/why-gm/why-2.webp'

import HeroImg from '@/assets/images/refresh/categories/womens-health/hero.webp'

export const womensHealthData: CategoryPageData = {
    supportWidget: {
        hideEyebrow: true,
        titleOverride: 'More from General Medicine',
    },
    generalAISm: {
        title: 'Not sure where to start? Let General AI help.',
        body: 'Curious whether your symptoms could be hormonal, cyclical, or something else? Chat with General AI to explore possible causes, learn about treatment paths, and prepare for a more productive visit with your clinician.',
    },
    getStartedCta: {
        heading: 'Talk to a clinician and start your healthcare journey today.',
        primaryCta: { label: 'Book a visit', href: '/visit' },
    },
    contentAccordion: {
        title: 'At General Medicine, you get a personalized care plan from a certified clinician.',

        subtitle:
            'Whether you’re looking for relief from a specific symptom or guidance through a major life transition, your care at General Medicine always starts with an expert clinician. They review your full health history, symptoms, labs, and lifestyle to build a plan that supports both short-term relief and keeps you prepared for what’s next.',
        images: [WhyImg2, WhyImg1],
        accordionItems: [
            {
                title: 'Share your health story',
                description:
                    '<p>Complete a brief medical questionnaire so our clinicians can understand your symptoms, health history, and goals—across every phase of your life.</p>',
            },
            {
                title: 'Meet your clinician',
                description:
                    '<p>Book a virtual visit with a licensed clinician who reviews your full picture, answers your questions, and helps determine the right next steps. Visits are designed to be thoughtful, not rushed, and may be covered by insurance.</p>',
            },
            {
                title: 'Get your care plan',
                description:
                    '<p>If appropriate, your clinician may recommend treatment options such as prescriptions, labs, or lifestyle guidance—tailored to your needs today and mindful of what’s ahead. Care plans are built to address both immediate symptoms and longer-term health.</p>',
            },
            {
                title: 'Start seeing results',
                description:
                    '<p>Check in anytime, adjust your care as your body evolves, and stay supported through each transition—whether you’re seeking relief now or planning ahead.</p>',
            },
        ],
    },
    categoryHeader: {
        showPrice: false,
        stickyBarLabel: {
            base: 'Get started today',
            sm: "Start your women's health journey today.",
        },
        title: 'Women’s health care, uniquely designed for every phase of life.',
        description:
            '<p>From cycle and fertility concerns to perimenopause and post-menopause, General Medicine delivers personalized women’s health care—on your schedule. Get answers, prescriptions, labs, and long-term support designed around your body, not a one-size-fits-all playbook. </p><p>Explore symptoms, understand your options, and meet with a clinician who sees the whole picture.</p>',
        primaryCta: { label: 'Book a visit', href: '/visit' },
        secondaryCta: {
            label: 'Ask General AI',
            href: '/ai',
            icon: 'sparkles',
        },
        anchorLinks: [
            { label: 'How it works', href: '#contentAccordion' },
            { label: 'Care Options', href: '#tabbedCardSliderV2' },
            { label: 'Testimonials', href: '#testimonialReviewsV2' },
            { label: 'FAQs', href: '#faqs' },
        ],
        images: [{ src: HeroImg, alt: 'Women’s health' }],
    },
    multiVideoPlayer: {
        title: 'Your questions, answered by our clinicians.',
        subtitle:
            'When it comes to women’s health, for so long, no one has focused on the data. That’s why we think it’s important for our physicians to break down the latest research, headlines, and health trends—what matters, what doesn’t, and what it means for your care.Hear your questions answered by General Medicine clinician, Dr. Pallabi Sanyal-Dey.',
        bgImage: WhyImage,
        videos: [
            {
                title: 'How do I know if I’m in perimenopause?',
                videoUrl:
                    'https://player.vimeo.com/progressive_redirect/playback/1084442319/rendition/1080p/file.mp4?loc=external&signature=85272d3fcfba01d06a19c6337a925405d2efb86fb0e022b31d8b4446bce093a8',
            },
            {
                title: 'What labs actually matter for women’s hormones?',
                videoUrl:
                    'https://player.vimeo.com/progressive_redirect/playback/1084442319/rendition/1080p/file.mp4?loc=external&signature=85272d3fcfba01d06a19c6337a925405d2efb86fb0e022b31d8b4446bce093a8',
            },
            {
                title: 'When should I consider hormone therapy?',
                videoUrl:
                    'https://player.vimeo.com/progressive_redirect/playback/1084442319/rendition/1080p/file.mp4?loc=external&signature=85272d3fcfba01d06a19c6337a925405d2efb86fb0e022b31d8b4446bce093a8',
            },
        ],
    },
    faq: {
        title: 'Frequently asked questions about women’s health.',
        items: [
            {
                question: 'Do I need a pelvic exam?',
                answer: 'The short answer is no. Some patients can have their symptoms resolved without a pelvic exam. The American College of Obstetricians and Gynecologists (ACOG) only recommends an in-person pelvic exam when you have symptoms or a medical history that requires it.',
            },
            {
                question: 'Is hormone therapy safe?',
                answer: 'Hormone therapy can be safe and effective for many women when appropriately prescribed. Your clinician will review your history, risks, and goals before recommending any treatment.',
            },
            {
                question: 'What is perimenopause?',
                answer: 'Perimenopause is the hormonal transition leading up to menopause. It typically begins in the mid-to-late 40s but can start earlier, and it lasts until a person has gone 12 consecutive months without a period. During this time, estrogen and progesterone levels fluctuate unpredictably, producing a range of physical and emotional symptoms.',
            },
            {
                question: 'What is menopause?',
                answer: 'Menopause is defined as 12 consecutive months without a menstrual period, marking the end of the reproductive years. It typically occurs between ages 45 and 55, with an average onset around age 51. The transition before menopause is called perimenopause, and the years after are called postmenopause. Most people continue to experience symptoms for several years after reaching menopause.',
            },
            {
                question: 'What is the difference between perimenopause and menopause care?',
                answer: 'Perimenopause care focuses on managing symptoms during an actively changing hormonal environment, where estrogen levels fluctuate unpredictably. Menopause care addresses symptoms and long-term health risks in the context of a sustained low-estrogen state. The treatment options overlap significantly, but the framing and long-term priorities can be different. If you’re unsure which stage you’re in, your physician can help clarify that during your visit.',
            },
            {
                question: 'How is General Medicine different from other platforms?',
                answer: 'Many providers treat menopause symptoms individually, prescribing something for hot flashes and leaving the rest unaddressed. At General Medicine, every menopause visit includes a full hormonal, metabolic, and long-term health evaluation. We look at how your symptoms connect to your bone health, your cardiovascular risk, your sleep, and your quality of life, and we build a plan that reflects all of it.',
            },
        ],
    },
    tabbedCardSlider: {
        title: 'Everything you need, in one place',
        description:
            'We offer clinician-guided care across the full spectrum of women’s health—organized by life stage and concern, with treatments backed by medical evidence.',
    },
    wholeBodyCarousel: {
        firstCard: {
            title: 'OB-Gyn consult',
            description: 'Our clinicians work directly with a specialist to get you an answer in hours, not months.',
        },
        otherCards: [
            {
                id: 'sleep-support',
                label: 'Sleep support',
                description: 'Treatments for insomnia and disrupted sleep cycles',
                image: SleepSupportImg,
                url: '/store/sleep',
            },
            {
                id: 'mood-mental-health',
                label: 'Mood & mental health',
                description: 'Care and treatment for anxiety, depression, stress, and more.',
                image: MoodMentalHealthImg,
                url: '/store/mental-health',
            },
            {
                id: 'memory-focus',
                label: 'Memory & focus',
                description: 'Evaluation and support to improve concentration and clarity.',
                image: MemoryFocusImg,
                url: 's/brain-fog-consult/300147',
            },
            {
                id: 'hair-loss',
                label: 'Hair loss',
                description: 'Targeted treatment for thinning and underlying causes.',
                image: HairLossImg,
                url: '/s/female-hair-loss/300035',
            },
            {
                id: 'skin-health',
                label: 'Skin health',
                description: 'Expert care for acne, rashes, moles, and more.',
                image: SkinHealthImg,
                url: '/store/skin',
            },
        ],
    },
}
